import type {
  AdAbandonDto,
  AdAbandonReason,
  ActivityDto,
  ActivityFilter,
  AdCompletionDto,
  AdSessionDto,
  AdSlotDto,
  AdSlotPurpose,
  BonusAdPurpose,
  CaptchaPassDto,
  CaptchaStatusDto,
  ClaimAllResultDto,
  ClaimResultDto,
  DailyCheckinClaimResultDto,
  DepositDto,
  DepositOverviewDto,
  RouletteDto,
  RouletteSpinResultDto,
  SavedAddressDto,
  SwapQuoteDto,
  SwapResultDto,
  DailyCheckinDto,
  DailyComboCheckResultDto,
  DailyComboDto,
  InterstitialSlotDto,
  AdexiumSlotDto,
  UslSlotDto,
  MineDto,
  MinerDto,
  MinerFilter,
  PageDto,
  ProfileDto,
  ReferralOverviewDto,
  ReferralUserDto,
  TaskDto,
  TaskListDto,
  TaskVerifyResultDto,
  WalletDto,
  WalletOverviewDto,
  WithdrawalDto,
} from '@pxc/shared';
import { newIdempotencyKey, request } from './api.js';

/** The answer to a presence beat: only how often to send the next one. */
export interface PresenceBeatDto {
  beatSeconds: number;
}

/** Every server call the app makes, in one typed place. */

export const api = {
  auth: {
    /** Ends every session of this account on the server. */
    logout: () => request<{ loggedOut: true }>('/auth/logout', { method: 'POST' }),
  },

  channel: {
    /** A fresh look at the official channel; out of it, the shell shows the screen to join. */
    check: () => request<{ member: boolean }>('/channel/check'),
  },

  mine: {
    get: () => request<MineDto>('/mine'),
  },

  miners: {
    list: (filter: MinerFilter = 'all') =>
      request<{ miners: MinerDto[] }>(`/miners?filter=${filter}`),
    get: (userMinerId: number) => request<{ miner: MinerDto }>(`/miners/${userMinerId}`),
  },

  ads: {
    createSession: (userMinerId: number) =>
      request<AdSessionDto>('/ads/sessions', {
        method: 'POST',
        body: { purpose: 'MINER_ACTIVATION', userMinerId },
      }),
    /** A bonus ad: the daily combo's reward or today's check-in, paid once the ad is confirmed. */
    createBonusSession: (purpose: BonusAdPurpose) =>
      request<AdSessionDto>('/ads/sessions', { method: 'POST', body: { purpose } }),
    /** An ad of the task centre's slots (GigaPub's, Adsgram's interstitial, Adexium's, USL's), paid per ad. */
    createSlotSession: (purpose: AdSlotPurpose) =>
      request<AdSessionDto>('/ads/sessions', { method: 'POST', body: { purpose } }),
    /** The GigaPub slot: what an ad pays, today's count, and when the next may start. */
    gigapub: () => request<AdSlotDto>('/ads/gigapub'),
    /** The Adsgram interstitial slot: the same, with its block and how long an ad must stay on screen. */
    interstitial: () => request<InterstitialSlotDto>('/ads/interstitial'),
    /** The Adexium slot: the same, with its widget. */
    adexium: () => request<AdexiumSlotDto>('/ads/adexium'),
    /** The USL slot: the same, with its key and placement. */
    usl: () => request<UslSlotDto>('/ads/usl'),
    /** Closes a session the app could not finish, so nothing counts it later. */
    abandon: (adSessionId: string, nonce: string, reason: AdAbandonReason) =>
      request<AdAbandonDto>(`/ads/sessions/${adSessionId}/abandon`, {
        method: 'POST',
        body: { nonce, reason },
      }),
    complete: (
      adSessionId: string,
      nonce: string,
      providerToken: string | null,
      click: { via: string | null; dwellMs: number | null } = { via: null, dwellMs: null },
    ) =>
      request<AdCompletionDto>(`/ads/sessions/${adSessionId}/complete`, {
        method: 'POST',
        body: {
          nonce,
          providerToken,
          clicked: click.via !== null,
          ...(click.via ? { clickVia: click.via } : {}),
          ...(click.dwellMs !== null ? { clickDwellMs: Math.max(0, Math.round(click.dwellMs)) } : {}),
        },
        idempotencyKey: newIdempotencyKey(),
      }),
  },

  /** The human check before a shift (Cloudflare Turnstile): whether it is due, and the answer handed over. */
  captcha: {
    status: () => request<CaptchaStatusDto>('/captcha'),
    verify: (token: string) =>
      request<CaptchaPassDto>('/captcha/verify', { method: 'POST', body: { token } }),
  },

  mining: {
    claim: (shiftPublicId: string, idempotencyKey: string) =>
      request<ClaimResultDto>(`/mining/shifts/${shiftPublicId}/claim`, {
        method: 'POST',
        idempotencyKey,
      }),
    claimAll: (idempotencyKey: string) =>
      request<ClaimAllResultDto>('/mining/claim-all', { method: 'POST', idempotencyKey }),
  },

  tasks: {
    list: () => request<TaskListDto>('/tasks'),
    start: (taskId: string) => request<{ task: TaskDto }>(`/tasks/${taskId}/start`, { method: 'POST' }),
    verify: (taskId: string, idempotencyKey: string) =>
      request<TaskVerifyResultDto>(`/tasks/${taskId}/verify`, { method: 'POST', idempotencyKey }),
  },

  checkin: {
    status: () => request<DailyCheckinDto>('/checkin'),
    claim: (idempotencyKey: string) =>
      request<DailyCheckinClaimResultDto>('/checkin/claim', { method: 'POST', idempotencyKey }),
  },

  combo: {
    status: () => request<DailyComboDto>('/combo'),
    check: (miners: string[], idempotencyKey: string) =>
      request<DailyComboCheckResultDto>('/combo/check', { method: 'POST', body: { miners }, idempotencyKey }),
  },

  roulette: {
    status: () => request<RouletteDto>('/roulette'),
    spin: (idempotencyKey: string) =>
      request<RouletteSpinResultDto>('/roulette/spin', { method: 'POST', body: {}, idempotencyKey }),
  },

  referrals: {
    overview: () => request<ReferralOverviewDto>('/referrals'),
    list: (cursor?: string | null, limit?: number) => {
      const params = new URLSearchParams();
      if (cursor) params.set('cursor', cursor);
      if (limit) params.set('limit', String(limit));
      const query = params.toString();
      return request<PageDto<ReferralUserDto>>(`/referrals/list${query ? `?${query}` : ''}`);
    },
  },

  wallet: {
    overview: () => request<WalletOverviewDto>('/wallet'),
    withdrawals: (cursor?: string | null) =>
      request<PageDto<WithdrawalDto>>(
        `/wallet/withdrawals${cursor ? `?cursor=${encodeURIComponent(cursor)}` : ''}`,
      ),
    withdraw: (
      input: { asset: string; network: string; address: string; amountMpxc: number },
      idempotencyKey: string,
    ) =>
      request<{ withdrawal: WithdrawalDto; wallet: WalletDto }>('/wallet/withdrawals', {
        method: 'POST',
        body: input,
        idempotencyKey,
      }),
    cancel: (publicId: string) =>
      request<{ withdrawal: WithdrawalDto; wallet: WalletDto }>(
        `/wallet/withdrawals/${publicId}/cancel`,
        { method: 'POST' },
      ),
    swapQuote: () => request<SwapQuoteDto>('/wallet/swap'),
    swap: (amountMpxc: number, asset: 'USDT' | 'TON', idempotencyKey: string) =>
      request<SwapResultDto>('/wallet/swap', { method: 'POST', body: { amountMpxc, asset }, idempotencyKey }),
    deposits: () => request<DepositOverviewDto>('/wallet/deposits'),
    reportDeposit: (
      input: { asset: 'USDT' | 'TON'; network: string; txHash: string; claimedAmountUnits: number | null },
      idempotencyKey: string,
    ) => request<{ deposit: DepositDto }>('/wallet/deposits', { method: 'POST', body: input, idempotencyKey }),
    addresses: () => request<{ items: SavedAddressDto[] }>('/wallet/addresses'),
    addAddress: (input: { asset?: string; network: string; address: string; label: string }) =>
      request<{ address: SavedAddressDto }>('/wallet/addresses', { method: 'POST', body: input }),
    removeAddress: (publicId: string) =>
      request<{ removed: true }>(`/wallet/addresses/${publicId}`, { method: 'DELETE' }),
  },

  activities: {
    list: (filter: ActivityFilter = 'all', cursor?: string | null) => {
      const params = new URLSearchParams({ type: filter });
      if (cursor) params.set('cursor', cursor);
      return request<PageDto<ActivityDto>>(`/activities?${params.toString()}`);
    },
  },

  presence: {
    /** "I am in the app": what the admin panel's live count is made of. */
    beat: () => request<PresenceBeatDto>('/presence/beat', { method: 'POST', body: {} }),
  },

  profile: {
    get: () => request<ProfileDto>('/profile'),
    updateSettings: (patch: {
      language?: string;
      haptics?: boolean;
      /** The mine's sound, and how loud (0–100). */
      sound?: boolean;
      volume?: number;
      notifications?: { mining?: boolean; tasks?: boolean; referrals?: boolean; reminders?: boolean; checkin?: boolean };
    }) => request<ProfileDto>('/profile/settings', { method: 'PATCH', body: patch }),
  },
};

/** Query keys, centralised so a mutation can never invalidate the wrong cache. */
export const queryKeys = {
  mine: ['mine'] as const,
  miners: (filter: MinerFilter) => ['miners', filter] as const,
  tasks: ['tasks'] as const,
  checkin: ['checkin'] as const,
  gigapub: ['ads', 'gigapub'] as const,
  interstitial: ['ads', 'interstitial'] as const,
  adexium: ['ads', 'adexium'] as const,
  usl: ['ads', 'usl'] as const,
  combo: ['combo'] as const,
  roulette: ['roulette'] as const,
  referrals: ['referrals'] as const,
  referralList: ['referrals', 'list'] as const,
  wallet: ['wallet'] as const,
  withdrawals: ['wallet', 'withdrawals'] as const,
  swapQuote: ['wallet', 'swap'] as const,
  deposits: ['wallet', 'deposits'] as const,
  addresses: ['wallet', 'addresses'] as const,
  activities: (filter: ActivityFilter) => ['activities', filter] as const,
  profile: ['profile'] as const,
};
